/**
 * Detects the best available SQLite mode based on browser capabilities.
 * Provides automatic fallback from OPFS to Worker mode when OPFS is unavailable.
 */
import { SqliteConfigurationError } from '../../errors';

export type SqliteMode = 'straight' | 'worker' | 'opfs' | 'native';

/**
 * Checks if the browser supports Origin Private File System (OPFS) with all required features.
 *
 * OPFS via sqlite-wasm's worker API requires:
 * - FileSystemHandle API (navigator.storage.getDirectory)
 * - SharedArrayBuffer support (for Worker-based OPFS)
 * - Atomics support
 * - COOP/COEP headers configured on the server
 *
 * @returns true if OPFS is fully supported, false otherwise
 */
export function isOpfsSupported(): boolean {
  try {
    // Check for basic OPFS API availability
    if (typeof navigator?.storage?.getDirectory !== 'function') {
      console.warn('[OPFS Detection] FileSystem Access API not available');
      return false;
    }

    // Check for SharedArrayBuffer support (required by sqlite-wasm OPFS worker)
    if (typeof SharedArrayBuffer === 'undefined') {
      console.warn(
        '[OPFS Detection] SharedArrayBuffer not available. ' +
          'The server must send COOP/COEP headers to enable OPFS:\n' +
          '  Cross-Origin-Opener-Policy: same-origin\n' +
          '  Cross-Origin-Embedder-Policy: require-corp',
      );
      return false;
    }

    // Check for Atomics support
    if (typeof Atomics === 'undefined') {
      console.warn('[OPFS Detection] Atomics API not available');
      return false;
    }

    // Check for required FileSystem APIs
    if (!globalThis.FileSystemHandle || !globalThis.FileSystemDirectoryHandle || !globalThis.FileSystemFileHandle) {
      console.warn('[OPFS Detection] FileSystem Handle APIs not available');
      return false;
    }

    console.info('[OPFS Detection] All OPFS requirements met ✓');
    return true;
  } catch (error) {
    console.warn('[OPFS Detection] Error checking support:', error);
    return false;
  }
}

/**
 * Validates that the requested SQLite mode is supported by the browser.
 * Throws an error if OPFS is requested but browser requirements aren't met.
 *
 * @param requestedMode - The desired SQLite mode
 * @throws Error if OPFS mode is requested but not supported
 */
export function validateSqliteMode(requestedMode: SqliteMode): void {
  // Only validate OPFS mode - straight, worker, and native have their own runtime checks.
  if (requestedMode !== 'opfs') {
    return;
  }

  // Check if OPFS is supported
  if (!isOpfsSupported()) {
    throw new SqliteConfigurationError(
      'OPFS mode is not supported in this browser or configuration.\n\n' +
        '⚠️  OPFS requires:\n' +
        '    • Modern browser with FileSystem Access API (Chrome 102+, Edge 102+, Opera 88+)\n' +
        '    • SharedArrayBuffer enabled via COOP/COEP headers:\n' +
        '      Cross-Origin-Opener-Policy: same-origin\n' +
        '      Cross-Origin-Embedder-Policy: require-corp\n\n' +
        '💡  To fix this:\n' +
        '    1. Change sqliteMode in environment.ts to "worker" or "straight"\n' +
        '    2. OR ensure COOP/COEP headers are configured in vite.config.mts\n' +
        '    3. Use a browser that supports OPFS (Chrome/Edge 102+)',
    );
  }
}
